<template>
  <div>
    <slot name="default" v-bind="{ inputClass }">
      <v-row>
        <v-col cols="12" class="pt-6 pb-2 px-2">
          <v-text-field
            style="margin-top: 4px; margin-bottom: 1px"
            dense
            label="تاریخ انقضا"
            v-model="formattedDisplayValue"
            :class="inputClass"
            :readonly="!editable"
            @input="handleTextInput"
            v-bind="$attrs"
            type="datetime"
            hide-details="انتخاب تاریخ الزامی است"
            placeholder="انتخاب تاریخ الزامی است"
          />
        </v-col>
        <v-col cols="12" class="pa-2">
          <v-btn
            class="rounded-pill btn-delete ma-0"
            depressed
            :loading="loading"
            @click="sendExpire"
          >
            <v-icon left>mdi-calendar-check</v-icon> ثبت تاریخ انقضا
          </v-btn>
        </v-col>
      </v-row>
    </slot>

    <date-picker
      v-model="tempValue"
      :auto-submit="false"
      :custom-input="`.${inputClass}`"
      :disable="disable"
      :display-format="getJFormat"
      :format="getFormat"
      :max="max"
      dense
      :min="min"
      :multiple="multiple"
      :range="$attrs.range"
      :type="format"
      outlined
      @input="updateValue"
    />
  </div>
</template>

<script>
import { debounce } from "@/main";
import moment from "moment-jalaali";

export default {
  name: "custom-date-picker-new",

  props: {
    qrId: [String, Number],
    value: [String, Array],
    autoSubmit: { type: Boolean, default: false },
    format: { type: String, default: "datetime" },
    inputClass: { type: String, default: "custom-input" },
    min: String,
    max: String,
    nullable: Boolean,
    sp: { type: String, default: "-" },
    editable: Boolean,
    multiple: Boolean,
    sortable: Boolean,
    disable: Array,
    expression: String,
  },

  data: () => ({
    date: "",
    jalaliDate: "",
    tempValue: null,
    loading: false,
    handleTextInput: null,
  }),

  created() {
    this.handleTextInput = debounce((val) => {
      if (!this.editable) return;
      if (val === null || val === undefined) return;
      try {
        moment.loadPersian({ dialect: "persian-modern" });
        const parsedDate = moment(val, [
          "jYYYY-jMM-jDD",
          "YYYY-MM-DD",
          "YYYY-M-D",
        ]);
        if (parsedDate.isValid()) {
          this.tempValue = parsedDate.format(this.getFormat);
          this.updateValue(this.tempValue);
        } else {
          console.warn("Invalid date input:", val);
        }
      } catch (e) {
        console.error("Error parsing text input:", e.message);
      }
    }, 300);
  },

  mounted() {
    moment.loadPersian({ dialect: "persian-modern" });
    if (!this.value && !this.nullable) {
      this.tempValue = this.nowDateTimeString(this.sp, this.format);
    } else {
      this.tempValue = this.value;
    }
  },

  computed: {
    dateString2: {
      get() {
        try {
          moment.loadPersian({ dialect: "persian-modern" });
          const formats = [
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DD HH:mm",
            "YYYY-MM-DD",
            "YYYY-M-D HH:mm:ss",
            "YYYY-M-D HH:mm",
            "YYYY-M-D",
            "jYYYY-jMM-jDD HH:mm:ss",
            "jYYYY-jMM-jDD HH:mm",
            "jYYYY-jMM-jDD",
          ];
          const parsedDate = moment(this.tempValue, formats, true);
          if (!parsedDate.isValid()) {
            return this.tempValue || "";
          }
          return (
            parsedDate.format("dddd LL") + " ساعت " + parsedDate.format("HH:mm")
          );
        } catch (e) {
          return this.tempValue || "";
        }
      },
      set(value) {
        try {
          moment.loadPersian({ dialect: "persian-modern" });
          const formats = [
            "jYYYY-jMM-jDD HH:mm:ss",
            "jYYYY-jMM-jDD HH:mm",
            "jYYYY-jMM-jDD",
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DD HH:mm",
            "YYYY-MM-DD",
            "YYYY-M-D HH:mm:ss",
            "YYYY-M-D HH:mm",
            "YYYY-M-D",
          ];
          const parsedDate = moment(value, formats, true);
          if (parsedDate.isValid()) {
            this.tempValue = parsedDate.format(this.getFormat);
            this.$emit("input", this.checkSort(this.tempValue));
          } else {
            console.warn("Invalid date input in dateString setter:", value);
            this.tempValue = null;
            this.$emit("input", null);
          }
        } catch (e) {
          console.error("Error in dateString setter:", e.message);
          this.tempValue = null;
          this.$emit("input", null);
        }
      },
    },
    getFormat() {
      if (this.expression) return this.expression;
      const sp = this.sp;
      switch (this.format) {
        case "datetime":
          return `YYYY${sp}MM${sp}DD HH:mm:ss`;
        case "date":
          return `YYYY${sp}MM${sp}DD`;
        case "time":
          return "HH:mm:ss";
        default:
          return `YYYY${sp}MM${sp}DD HH:mm:ss`;
      }
    },
    getJFormat() {
      const sp = this.sp;
      switch (this.format) {
        case "datetime":
          return `jYYYY${sp}jMM${sp}jDD HH:mm:ss`;
        case "date":
          return `jYYYY${sp}jMM${sp}jDD`;
        case "time":
          return "HH:mm:ss";
        default:
          return `jYYYY${sp}jMM${sp}jDD HH:mm:ss`;
      }
    },
    formattedDisplayValue() {
      if (!this.tempValue) return "";
      return this.dateString(this.tempValue);
    },
  },

  watch: {
    value(newVal) {
      this.tempValue = newVal;
    },
  },

  methods: {
    dateString() {
      try {
        moment.loadPersian({ dialect: "persian-modern" });
        const formats = [
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DD HH:mm",
          "YYYY-MM-DD",
          "YYYY-M-D HH:mm:ss",
          "YYYY-M-D HH:mm",
          "YYYY-M-D",
          "jYYYY-jMM-jDD HH:mm:ss",
          "jYYYY-jMM-jDD HH:mm",
          "jYYYY-jMM-jDD",
        ];
        const parsedDate = moment(this.tempValue, formats, true);
        if (!parsedDate.isValid()) {
          return this.tempValue || "";
        }
        return (
          parsedDate.format("dddd LL") + " ساعت " + parsedDate.format("HH:mm")
        );
      } catch (e) {
        console.error("Error in dateString getter:", e.message);
        return this.tempValue || "";
      }
    },
    async sendExpire() {
      if (!this.tempValue) {
        this.$toast.error("تاریخ انتخاب نشده است!");
        return;
      }
      this.loading = true;
      try {
        await this.$api.post(`/admin/change-expire/${this.qrId}`, {
          date: this.tempValue,
        });
        moment.loadPersian({ dialect: "persian-modern" });
        const expireDate = moment(this.tempValue, "YYYY-MM-DD HH:mm:ss");
        const now = moment();
        const expire_at_days = expireDate.diff(now, "days");
        this.$emit("submit", {
          date: this.tempValue,
          qrId: this.qrId,
          expire_at_days: expire_at_days >= 0 ? expire_at_days : 0,
        });
        this.$toast.success("تاریخ انقضا با موفقیت تغییر یافت.");
        this.$emit("close");
        this.tempValue = null;
      } catch (e) {
        this.$toast.error("خطا در تغییر تاریخ انقضا!");
      } finally {
        this.loading = false;
      }
    },
    updateValue(val) {
      if (val === this.tempValue) return;
      this.tempValue = val;
      this.$emit("input", this.checkSort(val));
    },
    checkSort(val) {
      if (!this.sortable || !val) return val;
      moment.loadPersian({ dialect: "persian-modern" });
      const dates = Array.isArray(val)
        ? val.map((d) => moment(d, this.getFormat)).sort((a, b) => a - b)
        : [moment(val, this.getFormat)];
      return Array.isArray(val)
        ? dates.map((item) => item.format("YYYY-MM-DD"))
        : dates[0].format("YYYY-MM-DD HH:mm:ss");
    },
    nowDateTimeString(sp, format) {
      moment.loadPersian({ dialect: "persian-modern" });
      return moment().format(
        format === "datetime"
          ? `YYYY${sp}MM${sp}DD HH:mm:ss`
          : `YYYY${sp}MM${sp}DD`,
      );
    },
  },
};
</script>

<style scoped></style>
